import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import { EventNote } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { ApiService } from "../services";
import { ItemFilter } from "../components/itemFilterComponent";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calendar.css";

const localizer = momentLocalizer(moment);

const defaultTimeRange = {
  from: "06:00",
  to: "23:00",
};

const calendarViews = [Views.MONTH, Views.WEEK, Views.DAY];

const formatHex = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  return value.charAt(0) === "#" ? value : "#" + value;
};

const getViewRange = (date, view) => {
  const baseDate = moment(date);

  if (view === Views.DAY) {
    return {
      start: baseDate.clone().startOf("day").toDate(),
      end: baseDate.clone().endOf("day").toDate(),
    };
  }

  if (view === Views.WEEK) {
    return {
      start: baseDate.clone().startOf("week").toDate(),
      end: baseDate.clone().endOf("week").toDate(),
    };
  }

  return {
    start: baseDate.clone().startOf("month").startOf("week").toDate(),
    end: baseDate.clone().endOf("month").endOf("week").toDate(),
  };
};

const getTimeBoundary = (timeValue, fallbackHour) => {
  const boundary = new Date();
  boundary.setSeconds(0);
  boundary.setMilliseconds(0);

  if (!timeValue) {
    boundary.setHours(fallbackHour, 0, 0, 0);
    return boundary;
  }

  const values = timeValue.split(":");
  boundary.setHours(parseInt(values[0], 10), parseInt(values[1], 10), 0, 0);
  return boundary;
};

const CalendarEvent = ({ event }) => (
  <div className="bookingCalendarEvent">
    <span className="bookingCalendarEvent__title">{event.title}</span>
    <span className="bookingCalendarEvent__meta">
      #{event.id} {event.customer?.title ? "· " + event.customer.title : ""}
    </span>
  </div>
);

export default function BookingCalendar() {
  const history = useHistory();
  const [view, setView] = useState(Views.WEEK);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filters, setFilters] = useState({
    product_id: "",
  });
  const [displayMode, setDisplayMode] = useState("timed");
  const [timeRange, setTimeRange] = useState(defaultTimeRange);
  const [calendarData, setCalendarData] = useState([]);
  const [summary, setSummary] = useState({ count: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const visibleRange = getViewRange(currentDate, view);
  const rangeStartIso = visibleRange.start.toISOString();
  const rangeEndIso = visibleRange.end.toISOString();

  useEffect(() => {
    setIsLoading(true);
    ApiService({
      method: "GET",
      route: "calendar/orders",
      body: {
        range_start: rangeStartIso,
        range_end: rangeEndIso,
        product_id: filters.product_id || "",
        status: "active",
      },
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          setCalendarData(response.data.data || []);
          setSummary(response.data.summary || { count: 0 });
        } else {
          setCalendarData([]);
          setSummary({ count: 0 });
        }
        setIsLoading(false);
      })
      .catch(() => {
        setCalendarData([]);
        setSummary({ count: 0 });
        setIsLoading(false);
      });
  }, [filters.product_id, rangeEndIso, rangeStartIso]);

  const events = calendarData.map((item) => {
    const start = new Date(item.start);
    const end = new Date(item.end);

    return {
      ...item,
      start: start,
      end: end,
      allDay: displayMode === "full-day",
    };
  });

  const minTime = getTimeBoundary(timeRange.from, 6);
  let maxTime = getTimeBoundary(timeRange.to, 23);
  if (maxTime <= minTime) {
    maxTime = getTimeBoundary("23:59", 23);
  }

  return (
    <div className="mainInnerView bookingCalendarPage">
      <h1 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <EventNote />
        Calendar
      </h1>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
          marginBottom: "18px",
          padding: "16px 18px",
          background:
            "linear-gradient(135deg, rgba(29,78,216,0.06), rgba(14,165,233,0.04))",
          border: "1px solid #e2e8f0",
          borderRadius: "16px",
        }}
      >
        <ItemFilter
          value={filters.product_id}
          handleItemChange={(value) =>
            setFilters({ ...filters, product_id: value || "" })
          }
          style={{ minWidth: "240px" }}
        />

        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontSize: "13px",
              fontWeight: "700",
              color: "#475569",
            }}
          >
            Display
          </span>
          <button
            type="button"
            onClick={() => setDisplayMode("timed")}
            style={{
              border: displayMode === "timed" ? "1px solid #1d4ed8" : "1px solid #d7ddea",
              background: displayMode === "timed" ? "#1d4ed8" : "#fff",
              color: displayMode === "timed" ? "#fff" : "#334155",
              borderRadius: "999px",
              padding: "9px 14px",
              fontSize: "13px",
              fontWeight: "700",
              cursor: "pointer",
            }}
          >
            Time Range
          </button>
          <button
            type="button"
            onClick={() => setDisplayMode("full-day")}
            style={{
              border:
                displayMode === "full-day" ? "1px solid #1d4ed8" : "1px solid #d7ddea",
              background: displayMode === "full-day" ? "#1d4ed8" : "#fff",
              color: displayMode === "full-day" ? "#fff" : "#334155",
              borderRadius: "999px",
              padding: "9px 14px",
              fontSize: "13px",
              fontWeight: "700",
              cursor: "pointer",
            }}
          >
            Full Day
          </button>
        </div>

        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontSize: "13px",
              fontWeight: "700",
              color: "#475569",
            }}
          >
            Hours
          </span>
          <input
            type="time"
            value={timeRange.from}
            disabled={displayMode === "full-day"}
            onChange={(event) =>
              setTimeRange({ ...timeRange, from: event.target.value })
            }
            style={{
              border: "1px solid #d7ddea",
              borderRadius: "10px",
              padding: "8px 10px",
              background: displayMode === "full-day" ? "#f8fafc" : "#fff",
            }}
          />
          <span style={{ color: "#64748b", fontWeight: "700" }}>to</span>
          <input
            type="time"
            value={timeRange.to}
            disabled={displayMode === "full-day"}
            onChange={(event) =>
              setTimeRange({ ...timeRange, to: event.target.value })
            }
            style={{
              border: "1px solid #d7ddea",
              borderRadius: "10px",
              padding: "8px 10px",
              background: displayMode === "full-day" ? "#f8fafc" : "#fff",
            }}
          />
        </div>

        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: "13px",
              fontWeight: "700",
              color: "#334155",
            }}
          >
            {summary.count || 0} active bookings
          </span>
          <span className="bookingCalendarLegend">Live booking statuses</span>
        </div>
      </div>

      <div
        style={{
          height: "calc(100vh - 220px)",
          minHeight: "620px",
          position: "relative",
        }}
      >
        <Calendar
          localizer={localizer}
          date={currentDate}
          events={events}
          view={view}
          views={calendarViews}
          startAccessor="start"
          endAccessor="end"
          popup={true}
          selectable={false}
          min={minTime}
          max={maxTime}
          step={30}
          timeslots={2}
          showMultiDayTimes={displayMode === "timed"}
          onView={(nextView) => setView(nextView)}
          onNavigate={(nextDate) => setCurrentDate(nextDate)}
          onSelectEvent={(event) =>
            history.push({
              pathname: event.booking_url,
              state: "booking",
            })
          }
          tooltipAccessor={(event) =>
            `${event.title}\n${event.starts_display} - ${event.ends_display}`
          }
          eventPropGetter={(event) => {
            const primary = formatHex(event.status_display?.color1, "#2563eb");
            const secondary = formatHex(event.status_display?.color2, "#dbeafe");

            return {
              style: {
                background: primary,
                color: "#fff",
                borderLeft: `4px solid ${secondary}`,
              },
            };
          }}
          components={{
            event: CalendarEvent,
          }}
        />
        {isLoading ? (
          <div className="bookingCalendarLoading">
            <div className="bookingCalendarLoading__card">
              <div className="bookingCalendarLoading__dot" />
              Loading bookings...
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
    </div>
  );
}
